import { useMutation, useQuery } from '@tanstack/react-query';
import { Form } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Filter } from '../../../../@types/filter';
import { positionApi } from '../../../../apis';
import { CreatePositionDto, Position, UpdatePositionDto } from '../../../../apis/client-axios';
import ButtonWrap from '../../../../components/ButtonWrap';
import InputWrap from '../../../../components/InputWrap';
import ModalWrap from '../../../../components/ModalWrap';
import SvgAdd from '../../../../components/svg/SvgAdd';
import SvgDelete from '../../../../components/svg/SvgDelete';
import SvgEdit from '../../../../components/svg/SvgEdit';
import TableWrap from '../../../../components/TableWrap';
import ToastError from '../../../../components/Toast/ToastError';
import ToastSuccess from '../../../../components/Toast/ToastSuccess';
import Widget from '../../../../components/Widget';
import { QUERY_KEY } from '../../../../constants/QueryKey';
import './style.scss';

const PositionManagement = () => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<Position | null>(null);
  const [filter, setFilter] = useState<Filter>({ page: 1, pageSize: 20, fullTextSearch: '' });

  const {
    data: dataPosition,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [QUERY_KEY.POSITION_MANAGEMENT, filter],
    queryFn: () => {
      return positionApi.positionControllerFindAll(
        filter.page,
        filter.pageSize,
        undefined,
        filter.fullTextSearch,
        undefined,
      );
    },
  });

  const addMutation = useMutation({
    mutationFn: (payload: CreatePositionDto) => positionApi.positionControllerCreate(payload),
    onSuccess: () => {
      setOpenAddModal(false);
      refetch();
      form.resetFields();
      ToastSuccess({
        content: intl.formatMessage({ id: 'positionManagement.addSuccees' }),
      });
    },
    onError: (error: { response: { data: { message: string; errorCode: string }; status: number } }) => {
      if (error.response.data.errorCode.includes('EXISTED')) {
        ToastError({
          content: intl.formatMessage({ id: 'positionManagement.beenCreated' }),
        });
      }
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdatePositionDto }) =>
      positionApi.positionControllerUpdate(id, payload),
    onSuccess: () => {
      setOpenEditModal(false);
      refetch();
      ToastSuccess({
        content: intl.formatMessage({ id: 'positionManagement.editSuccees' }),
      });
    },
    onError: (error: { response: { data: { message: string; errorCode: string }; status: number } }) => {
      if (error.response.data.errorCode.includes('EXISTED')) {
        ToastError({
          content: intl.formatMessage({ id: 'positionManagement.beenCreated' }),
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => positionApi.positionControllerRemove(id),
    onSuccess: () => {
      setFilter((prevFilter) => ({
        ...prevFilter,
        page: 1,
      }));
      setOpenDeleteModal(false);
      refetch();
      ToastSuccess({
        content: intl.formatMessage({ id: 'positionManagement.deleteSuccees' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'positionManagement.deleteFail' }),
      });
    },
  });

  const handleAddPosition = () => {
    form
      .validateFields()
      .then((values) => {
        const payload: CreatePositionDto = {
          name: form.getFieldValue('addPosition'),
        };
        addMutation.mutate(payload);
      })
      .catch((error) => {
        console.error('Validate Failed:', error);
      });
  };

  const handleEditPosition = () => {
    form
      .validateFields()
      .then((values) => {
        const payload: UpdatePositionDto = {
          name: form.getFieldValue('editPosition'),
        };
        editMutation.mutate({ id: selectedRowData?.id as string, payload });
      })
      .catch((error) => {
        console.error('Validate Failed:', error);
      });
  };

  const handleDeletePosition = () => {
    deleteMutation.mutate(selectedRowData?.id as string);
  };

  const columns = [
    {
      title: intl.formatMessage({ id: 'positionManagement.title' }),
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => {
        return (
          <div className="position-management-content-name">
            <p className="position-management-content-name-tool">{record?.name}</p>
          </div>
        );
      },
    },
    {
      title: '',
      key: 'action',
      render: (record, row) => (
        <div className="position-management-content-data-options">
          <ButtonWrap
            className="position-management-content-button-edit"
            onClick={() => {
              setOpenEditModal(true);
              setSelectedRowData(row);
              form.setFieldValue('editPosition', row.name);
            }}
          >
            <SvgEdit /> {intl.formatMessage({ id: 'positionManagement.editButton' })}
          </ButtonWrap>
          <ButtonWrap
            className="position-management-content-button-delete"
            onClick={() => {
              setOpenDeleteModal(true);
              setSelectedRowData(row);
            }}
          >
            <SvgDelete />
            {intl.formatMessage({ id: 'positionManagement.deleteButton' })}
          </ButtonWrap>
        </div>
      ),
    },
  ];
  return (
    <div className="position-management">
      <div className="position-management-content">
        <Widget
          breadcrumbs={[
            intl.formatMessage({ id: 'adminMenu.staffDepartmentPosition' }),
            intl.formatMessage({ id: 'positionManagement.title' }),
          ]}
          content={
            <TableWrap
              isPagination
              loading={isLoading}
              onSearch={(search) => {
                setFilter((prevFilters) => ({
                  ...prevFilters,
                  page: 1,
                  fullTextSearch: search,
                }));
              }}
              paginationProps={{
                current: dataPosition?.data?.data?.page || 1,
                pageSize: dataPosition?.data?.data?.size || 20,
                total: dataPosition?.data?.data?.total || 0,
                onChange: (page, pageSize) =>
                  setFilter((prevFilters) => ({
                    ...prevFilters,
                    page,
                    pageSize,
                  })),
              }}
              inputProps={{
                placeholder: intl.formatMessage({ id: 'positionManagement.searchBox' }),
              }}
              tableProps={{
                dataSource: Array.isArray(dataPosition?.data?.data?.content) ? dataPosition?.data?.data?.content : [],
                columns,
                className: 'position-management-content-table',
              }}
              rightTopContent={
                <>
                  <ButtonWrap
                    onClick={() => {
                      setOpenAddModal(true);
                    }}
                    variant="outlined"
                    className="position-management-content-button-add"
                  >
                    <SvgAdd /> {intl.formatMessage({ id: 'positionManagement.addButton' })}
                  </ButtonWrap>
                </>
              }
            />
          }
          title={intl.formatMessage({ id: 'positionManagement.title' })}
        />
      </div>
      <div className="position-management-content-modal">
        {openAddModal && (
          <ModalWrap
            open={openAddModal}
            cancelText={intl.formatMessage({ id: 'positionManagement.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'positionManagement.addButton' })}
            buttonSubmitProps={{ disabled: addMutation.isPending }}
            onSubmit={handleAddPosition}
            onCancel={() => {
              setOpenAddModal(false);
              form.resetFields();
            }}
            type="upsert"
            title={intl.formatMessage({ id: 'positionManagement.addButtonTitle' })}
            className="position-management-content-modal-add"
            body={
              <Form form={form} layout="vertical">
                <p className="position-management-content-title">
                  {intl.formatMessage({ id: 'positionManagement.title' })}
                  <span className="label_required">*</span>
                </p>
                <Form.Item
                  name="addPosition"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'common.required' }),
                    },
                    {
                      whitespace: true,
                      message: intl.formatMessage({ id: 'positionManagement.invalid' }),
                    },
                  ]}
                >
                  <InputWrap
                    className="position-management-content-modal-add-input"
                    placeholder={intl.formatMessage({ id: 'positionManagement.inputPlaceholder' })}
                    required={true}
                    maxLength={255}
                  />
                </Form.Item>
              </Form>
            }
          />
        )}
      </div>
      <div className="position-management-content-modal">
        {openEditModal && (
          <ModalWrap
            open={openEditModal}
            cancelText={intl.formatMessage({ id: 'positionManagement.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'positionManagement.editButton' })}
            buttonSubmitProps={{ disabled: editMutation.isPending }}
            onSubmit={handleEditPosition}
            onCancel={() => {
              setOpenEditModal(false);
              form.resetFields();
            }}
            type="upsert"
            title={intl.formatMessage({ id: 'positionManagement.editButtonTitle' })}
            className="position-management-content-modal-add"
            body={
              <Form form={form} layout="vertical">
                <p className="position-management-content-title">
                  {intl.formatMessage({ id: 'positionManagement.title' })}
                  <span className="label_required">*</span>
                </p>
                <Form.Item
                  name="editPosition"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'common.required' }),
                    },
                    {
                      whitespace: true,
                      message: intl.formatMessage({ id: 'positionManagement.invalid' }),
                    },
                  ]}
                >
                  <InputWrap
                    className="position-management-content-modal-add-input"
                    placeholder={intl.formatMessage({ id: 'positionManagement.inputPlaceholder' })}
                    required={true}
                    maxLength={255}
                  />
                </Form.Item>
              </Form>
            }
          />
        )}
      </div>
      <div className="position-management-content-modal">
        {openDeleteModal && (
          <ModalWrap
            open={openDeleteModal}
            cancelText={intl.formatMessage({ id: 'positionManagement.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'positionManagement.deleteButton' })}
            buttonSubmitProps={{ disabled: deleteMutation.isPending }}
            onSubmit={handleDeletePosition}
            onCancel={() => setOpenDeleteModal(false)}
            type="upsert"
            title={intl.formatMessage({ id: 'positionManagement.deleteButtonTitle' })}
            className="position-management-content-modal-delete"
            body={
              <div className="position-management-content-modal-delete-body">
                <p className="position-management-content-modal-delete-title">
                  <span className="position-management-content-name-tool name">{selectedRowData?.name}</span>
                  {intl.formatMessage({ id: 'booking.modal.delete' })}
                </p>
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

export default PositionManagement;
